// Admin v2 — felhasználói műveletek és modálok (Figma ADM_02b–02d):
// szerkesztés, új felhasználó, helyszín hozzárendelése, szerepváltás, törlés.
import React, { useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, setDoc, updateDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { db, secondaryAuth } from '../../firebase';
import { AdminUser, Role, ROLE_LABEL, roleFields, roleOf } from '../data';
import { Avatar, Button, ConfirmModal, Field, Input, MenuItem, Modal, Notice, PasswordInput, Select, TextArea, Toggle, useToast } from '../ui';
import { Icon } from '../icons';

export interface Venue { id: string; name: string }

export async function loadActiveVenues(): Promise<Venue[]> {
  const s = await getDocs(collection(db, 'venues'));
  return s.docs
    .filter((d) => (d.data().status ?? 'active') === 'active')
    .map((d) => ({ id: d.id, name: String(d.data().name ?? 'Helyszín') }))
    .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
}

/** CP-szint a CP-egyenlegből, az app szabálya szerint: floor(max(0, CP) / 50) + 1. */
const cpLevel = (cp: number) => Math.floor(Math.max(0, cp) / 50) + 1;

/* ---------- Adatok szerkesztése (ADM_02c) ---------- */
export function EditUserModal({ user, onClose, onSaved }: { user: AdminUser; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({
    displayName: user.displayName, username: user.username, city: user.city, bio: user.bio,
    role: roleOf(user) as Role, isPremium: user.isPremium,
    dqScore: String(user.dqScore), dqLevel: String(user.dqLevel), currentXP: String(user.currentXP),
  });
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    // friss dokumentum (a lista lehet, hogy régebbi)
    getDoc(doc(db, 'users', user.uid)).then((s) => {
      if (!s.exists()) return;
      const v = s.data();
      const ob = (v.onboarding ?? {}) as Record<string, unknown>;
      setF((x) => ({
        ...x,
        city: String(ob.city ?? v.city ?? x.city ?? ''),
        bio: String(ob.bio ?? v.bio ?? x.bio ?? ''),
      }));
    }).catch(() => undefined);
  }, [user.uid]);

  const save = async () => {
    setBusy(true);
    try {
      const prevRole = roleOf(user);
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: f.displayName.trim(),
        username: f.username.trim().toLowerCase(),
        'onboarding.city': f.city.trim(),
        'onboarding.bio': f.bio.trim(),
        isPremium: f.isPremium,
        dqScore: Number(f.dqScore) || 0,
        dqLevel: cpLevel(Number(f.dqScore) || 0), // a szint mindig a CP-ből számolódik (floor(CP/50)+1)
        currentXP: Number(f.currentXP) || 0,
        ...(f.role !== prevRole ? { ...roleFields(f.role, user.venueIds), previousRole: prevRole } : {}),
      });
      toast('Módosítások mentve.');
      onSaved();
      onClose();
    } catch (e) {
      console.warn('[users] mentés', e);
      toast('A mentés nem sikerült.', 'red');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Adatok szerkesztése"
      onClose={onClose}
      width={560}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button onClick={save} disabled={busy}>Mentés</Button></>}
    >
      <div className="flex items-center gap-2.5 mb-5">
        <Avatar src={user.avatarUrl} name={user.displayName} size={32} />
        <div>
          <div className="text-[14px] font-semibold text-white">{user.displayName}</div>
          <div className="text-[12px] text-[#8a8f99]">{user.email}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Megjelenített név"><Input value={f.displayName} onChange={(e) => set('displayName', e.target.value)} /></Field>
        <Field label="Felhasználónév"><Input value={f.username} onChange={(e) => set('username', e.target.value)} /></Field>
        <Field label="Város"><Input value={f.city} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field label="Szerep">
          <Select value={f.role} onChange={(v) => set('role', v)}
            options={(['user', 'admin', 'venueOperator', 'coach'] as Role[]).map((r) => ({ value: r, label: ROLE_LABEL[r] }))} />
        </Field>
        <Field label="Bemutatkozás" className="col-span-2"><TextArea value={f.bio} onChange={(e) => set('bio', e.target.value)} rows={2} /></Field>
      </div>
      <div className="mt-4 flex items-center justify-between px-4 h-[52px] rounded-xl bg-[#1d232a]">
        <span className="text-[14px] font-semibold text-white">Prémium fiók</span>
        <Toggle checked={f.isPremium} onChange={(v) => set('isPremium', v)} />
      </div>
      <h4 className="mt-5 mb-3 text-[15px] font-extrabold italic uppercase text-white">Játékos-értékek</h4>
      <div className="grid grid-cols-3 gap-3">
        <Field label="CP"><Input inputMode="numeric" value={f.dqScore} onChange={(e) => set('dqScore', e.target.value.replace(/[^\d]/g, ''))} /></Field>
        <Field label="Szint (a CP-ből)"><Input value={`CP${cpLevel(Number(f.dqScore) || 0)}`} readOnly disabled /></Field>
        <Field label="XP"><Input inputMode="numeric" value={f.currentXP} onChange={(e) => set('currentXP', e.target.value.replace(/[^\d]/g, ''))} /></Field>
      </div>
      <div className="mt-4"><Notice>Kézi módosítás felülírja a meccsekből számolt értékeket.</Notice></div>
      {f.role === 'venueOperator' && roleOf(user) !== 'venueOperator' && (
        <p className="mt-3 text-[12px] text-[#8a8f99]">Mentés után a ⋮ menüben rendeld hozzá a helyszíneit.</p>
      )}
    </Modal>
  );
}

/* ---------- Felhasználó hozzáadása (ADM_02d) ---------- */
// A második Firebase-példánnyal (secondaryAuth) hozza létre a fiókot, így az admin bejelentkezve marad.
export function AddUserModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setMsg('');
    if (!name.trim()) return setMsg('Add meg a teljes nevet.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setMsg('Adj meg egy érvényes e-mail címet.');
    if (password.length < 6) return setMsg('A jelszó legalább 6 karakter legyen.');
    setBusy(true);
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email.trim(), password);
      await updateProfile(cred.user, { displayName: name.trim() }).catch(() => undefined);
      await setDoc(doc(db, 'users', cred.user.uid), {
        uid: cred.user.uid,
        email: email.trim().toLowerCase(),
        displayName: name.trim(),
        username: email.trim().split('@')[0].toLowerCase(),
        isAdmin: false,
        isVenueOperator: false,
        isCoach: false,
        role: 'user',
        venueIds: [],
        dqScore: 100,
        dqLevel: cpLevel(100), // kezdő 100 CP = CP3
        createdAt: new Date().toISOString(),
      });
      await signOut(secondaryAuth).catch(() => undefined);
      toast('Felhasználó létrehozva.');
      onSaved();
      onClose();
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      setMsg(code === 'auth/email-already-in-use' ? 'Ezzel az e-mail címmel már van fiók.'
        : code === 'auth/weak-password' ? 'A jelszó túl gyenge.'
          : 'A létrehozás nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Felhasználó hozzáadása"
      onClose={onClose}
      width={460}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button icon="UserPlus" onClick={add} disabled={busy}>Felhasználó hozzáadása</Button></>}
    >
      <div className="flex flex-col gap-4">
        <Field label="Teljes név"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Kiss János" /></Field>
        <Field label="Email"><Input icon="Envelope" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" /></Field>
        <Field label="Jelszó"><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 karakter" autoComplete="new-password" /></Field>
      </div>
      {msg && <div className="mt-4 text-[13px] text-[#ef4444]" role="alert">{msg}</div>}
    </Modal>
  );
}

/* ---------- Helyszín hozzárendelése ---------- */
export function VenueAssignModal({ user, onClose, onSaved }: { user: AdminUser; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [sel, setSel] = useState<string[]>(user.venueIds);
  const [busy, setBusy] = useState(false);
  useEffect(() => { loadActiveVenues().then(setVenues).catch(() => setVenues([])); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), sel.length
        ? { isVenueOperator: true, venueIds: sel, role: user.isAdmin ? 'admin' : 'venueOperator' }
        : { isVenueOperator: false, venueIds: [], venueId: null, role: user.isAdmin ? 'admin' : user.isCoach ? 'coach' : 'user' });
      toast(sel.length ? 'Helyszínek hozzárendelve.' : 'Üzemeltetői jogosultság elvéve.');
      onSaved();
      onClose();
    } catch (e) {
      console.warn('[users] helyszín', e);
      toast('A mentés nem sikerült.', 'red');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Helyszín hozzárendelése"
      onClose={onClose}
      width={480}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button onClick={save} disabled={busy || venues === null}>Mentés</Button></>}
    >
      <p className="text-[13px] text-[#B8B8C3] mb-4">
        <span className="text-white font-semibold">{user.displayName}</span> üzemeltetőként ezeket a helyszíneket kezeli:
      </p>
      {venues === null && <div className="text-[13px] text-[#8a8f99]">Betöltés…</div>}
      {venues?.length === 0 && <div className="text-[13px] text-[#8a8f99]">Nincs aktív helyszín.</div>}
      <div className="flex flex-col gap-2 max-h-[340px] overflow-y-auto">
        {venues?.map((v) => {
          const on = sel.includes(v.id);
          return (
            <button key={v.id} type="button"
              onClick={() => setSel((s) => (on ? s.filter((x) => x !== v.id) : [...s, v.id]))}
              className={`flex items-center gap-3 h-[44px] px-4 rounded-xl border text-left text-[14px] transition ${on ? 'bg-[#00AFF2]/[0.12] border-[#00AFF2]/40 text-white' : 'bg-[#1d232a] border-[#31383D] text-[#B8B8C3] hover:text-white'}`}>
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md border ${on ? 'bg-[#00AFF2] border-[#00AFF2] text-white' : 'border-[#31383D]'}`}>
                {on && <Icon name="Check" size={14} />}
              </span>
              <Icon name="MapPin" size={16} className="text-[#d9a514]" />
              {v.name}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

/* ---------- ⋮ menü + a hozzá tartozó modálok egy helyen ---------- */
export type Dialog = { kind: 'edit' | 'venue' | 'delete' | 'admin' | 'coach' | 'operatorOff'; user: AdminUser } | null;

export function userMenuItems(u: AdminUser, open: (d: Dialog) => void, extra: MenuItem[] = []): MenuItem[] {
  return [
    ...extra,
    { icon: 'NotePencil', label: 'Adatok szerkesztése', onClick: () => open({ kind: 'edit', user: u }) },
    { icon: 'ShieldStar', label: u.isAdmin ? 'Admin jogosultság elvétele' : 'Admin jogosultság adása', onClick: () => open({ kind: 'admin', user: u }) },
    u.isVenueOperator
      ? { icon: 'Buildings', label: 'Üzemeltető jogosultság elvétele', onClick: () => open({ kind: 'operatorOff', user: u }) }
      : { icon: 'Buildings', label: 'Üzemeltető jogosultság adása', onClick: () => open({ kind: 'venue', user: u }) },
    { icon: 'MapPin', label: 'Helyszín hozzárendelése', onClick: () => open({ kind: 'venue', user: u }) },
    { icon: 'GraduationCap', label: u.isCoach ? 'Edző jogosultság elvétele' : 'Edző jogosultság adása', onClick: () => open({ kind: 'coach', user: u }) },
    { icon: 'Trash', label: 'Felhasználó törlése', danger: true, onClick: () => open({ kind: 'delete', user: u }) },
  ];
}

export function UserDialogs({ dialog, close, reload, selfUid, onDeleted }: {
  dialog: Dialog; close: () => void; reload: () => void; selfUid?: string; onDeleted?: () => void;
}) {
  const toast = useToast();
  if (!dialog) return null;
  const u = dialog.user;
  const run = async (patch: Record<string, unknown>, ok: string) => {
    try {
      await updateDoc(doc(db, 'users', u.uid), { ...patch, previousRole: roleOf(u) });
      toast(ok);
      reload();
    } catch (e) {
      console.warn('[users] jogosultság', e);
      toast('A módosítás nem sikerült.', 'red');
    }
    close();
  };

  switch (dialog.kind) {
    case 'edit':
      return <EditUserModal user={u} onClose={close} onSaved={reload} />;
    case 'venue':
      return <VenueAssignModal user={u} onClose={close} onSaved={reload} />;
    case 'admin':
      return (
        <ConfirmModal
          title={u.isAdmin ? 'Admin jogosultság elvétele' : 'Admin jogosultság adása'}
          text={u.uid === selfUid && u.isAdmin
            ? 'A saját admin jogosultságodat veszed el. Ezután nem fogod látni az admin felületet.'
            : <><b className="text-white">{u.displayName}</b> {u.isAdmin ? 'elveszíti az admin hozzáférést.' : 'teljes admin hozzáférést kap.'}</>}
          confirmLabel={u.isAdmin ? 'Elvétel' : 'Jogosultság adása'}
          danger={u.isAdmin}
          onClose={close}
          onConfirm={() => run({ isAdmin: !u.isAdmin, role: !u.isAdmin ? 'admin' : u.isVenueOperator ? 'venueOperator' : u.isCoach ? 'coach' : 'user' },
            u.isAdmin ? 'Admin jogosultság elvéve.' : 'Admin jogosultság megadva.')}
        />
      );
    case 'coach':
      return (
        <ConfirmModal
          title={u.isCoach ? 'Edző jogosultság elvétele' : 'Edző jogosultság adása'}
          text={<><b className="text-white">{u.displayName}</b> {u.isCoach ? 'többé nem jelenik meg edzőként.' : 'edzői profilt kap és edzéseket hirdethet.'}</>}
          confirmLabel={u.isCoach ? 'Elvétel' : 'Jogosultság adása'}
          danger={u.isCoach}
          onClose={close}
          onConfirm={() => run(
            u.isCoach
              ? { isCoach: false, coachVenues: null, role: u.isAdmin ? 'admin' : u.isVenueOperator ? 'venueOperator' : 'user' }
              : { isCoach: true, coachVenues: [], role: u.isAdmin ? 'admin' : u.isVenueOperator ? 'venueOperator' : 'coach' },
            u.isCoach ? 'Edző jogosultság elvéve.' : 'Edző jogosultság megadva.')}
        />
      );
    case 'operatorOff':
      return (
        <ConfirmModal
          title="Üzemeltető jogosultság elvétele"
          text={<><b className="text-white">{u.displayName}</b> nem kezeli tovább a hozzárendelt helyszíneket.</>}
          confirmLabel="Elvétel"
          danger
          onClose={close}
          onConfirm={() => run({ isVenueOperator: false, venueIds: [], venueId: null, role: u.isAdmin ? 'admin' : u.isCoach ? 'coach' : 'user' },
            'Üzemeltetői jogosultság elvéve.')}
        />
      );
    case 'delete':
      return (
        <ConfirmModal
          title="Felhasználó törlése"
          text={<>Biztosan törlöd <b className="text-white">{u.displayName}</b> profilját? A felhasználói dokumentum véglegesen törlődik; a bejelentkezési fiókot a Firebase konzolban lehet megszüntetni.</>}
          confirmLabel="Törlés"
          danger
          onClose={close}
          onConfirm={async () => {
            try {
              await deleteDoc(doc(db, 'users', u.uid));
              toast('Felhasználó törölve.');
              onDeleted ? onDeleted() : reload();
            } catch (e) {
              console.warn('[users] törlés', e);
              toast('A törlés nem sikerült.', 'red');
            }
            close();
          }}
        />
      );
    default:
      return null;
  }
}
